import { useQuery, useQueryClient, type QueryFunctionContext } from "@tanstack/react-query";
import { useEffect } from "react";
import { SignalRGroup } from "src/components";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import { FetchError, queryKeys, useAccessToken, type FormSubmissionDetailDto } from "src/http";

const useGetFormSubmission = (formSubmissionGuid: string | null) => {
  const { getAccessToken } = useAccessToken();
  const signalR = useSignalR();
  const queryClient = useQueryClient();

  async function getFormSubmissionRequest({
    queryKey,
  }: QueryFunctionContext<
    ReturnType<typeof queryKeys.formSubmissions.getFormSubmission>
  >): Promise<FormSubmissionDetailDto> {
    const [{ formSubmissionGuid }] = queryKey;
    const accessToken = await getAccessToken();

    const response = await fetch(`${configuration.apiRootUrl}/form-submissions/${formSubmissionGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response.json();
  }

  useEffect(() => {
    const handler = (formSubmissionGuid: string, updates: Partial<FormSubmissionDetailDto>) => {
      queryClient.setQueryData<FormSubmissionDetailDto>(
        queryKeys.formSubmissions.getFormSubmission(formSubmissionGuid),
        (cachedData) => {
          if (cachedData) {
            return {
              ...cachedData,
              ...updates,
            };
          } else {
            return cachedData;
          }
        },
      );
    };

    if (formSubmissionGuid != null) {
      signalR.joinGroup(SignalRGroup.FormSubmissionDetail(formSubmissionGuid));
      signalR.onShouldUpdateFormSubmissionDetail(handler);
    }

    return () => {
      if (formSubmissionGuid != null) {
        signalR.leaveGroup(SignalRGroup.FormSubmissionDetail(formSubmissionGuid));
        signalR.offShouldUpdateFormSubmissionDetail(handler);
      }
    };
  }, [formSubmissionGuid, queryClient, signalR]);

  return useQuery({
    queryKey: queryKeys.formSubmissions.getFormSubmission(formSubmissionGuid),
    queryFn: getFormSubmissionRequest,
    enabled: formSubmissionGuid != null,
  });
};

export { useGetFormSubmission };
